import { merge } from 'lodash-es';
import { type CSSProperties, useId } from 'react';

import type { CSS } from '../../stitches.config';
import type { GutterProp } from '../../utilities/shared/Gutter';
import type { PaddingProps } from '../../utilities/shared/Padding';
import type { SpacingScale } from '../../utilities/shared/sizes';
import { fade as colorFade, palette } from '../../common/colors';
import { darkThemeSelector, keyframes, styled } from '../../stitches.config';
import { gutterCSS } from '../../utilities/shared/Gutter';
import { paddingCSS } from '../../utilities/shared/Padding';
import { spacingCSS } from '../../utilities/shared/Spacing';

const skeletonGrandient = keyframes({
  '0%': {
    backgroundPosition: '0% 50%',
  },
  '50%': {
    backgroundPosition: '100% 50%',
  },
  '100%': {
    backgroundPosition: '0% 50%',
  },
});

const SkeletonContainer = styled('div', {
  backgroundImage: `linear-gradient(-45deg, ${colorFade(palette.gray300, 0.25)}, ${colorFade(palette.gray200, 0.1)}, ${colorFade(palette.gray300, 0.25)})`,
  backgroundSize: '400% 400%',
  animation: `${skeletonGrandient} 2s linear infinite`,

  [darkThemeSelector]: {
    backgroundImage: `linear-gradient(-45deg, ${colorFade(palette.gray700, 0.35)}, ${colorFade(palette.gray800, 0.2)}, ${colorFade(palette.gray700, 0.35)})`,
  },
});
SkeletonContainer.displayName = 'SkeletonContainer';

export interface SkeletonProps {
  /**
   * Set the display type of the box modal.
   */
  display?: CSSProperties['display'];
  /**
   * A string to represent the height as a CSS value.
   */
  height?: CSSProperties['height'];
  /**
   * The opacity of the skeletons.
   */
  opacity?: CSSProperties['opacity'];
  /**
   * Set the border radius of the component.
   */
  radius?: CSSProperties['borderRadius'];
  /**
   * A string to represent the width as a CSS value.
   */
  width?: CSSProperties['width'];
}

export function Skeleton({ display, height, opacity, radius, width, ...remaining }: SkeletonProps) {
  return (
    <SkeletonContainer
      style={{
        width,
        height,
        display,
        borderRadius: radius,
        opacity,
      }}
      {...remaining}
    />
  );
}

const SkeletonGroupContainer = styled('div', {
  display: 'flex',
  maxWidth: '100%',
  maxHeight: '100%',
  overflow: 'hidden',
});
SkeletonGroupContainer.displayName = 'SkeletonGroupContainer';

type SkeletonGroupProps = {
  /**
   * The context of the skeletons.
   */
  context?: 'application' | 'content';
  /**
   * The direction of the skeletons.
   */
  direction?: CSSProperties['flexDirection'];
  /**
   * The gutter of the skeletons.
   */
  gutter?: GutterProp;
  /**
   * The padding of the skeletons.
   */
  padding?: PaddingProps;
  /**
   * The spacing between the skeletons.
   */
  spacing?: SpacingScale;
  /**
   * The skeletons to render.
   */
  skeletons: SkeletonProps[];
  ref?: React.Ref<HTMLDivElement | null>;
  /**
   * Fade the loaders out as they progress.
   */
  fade?: boolean;
};

export function SkeletonGroup({
  context = 'application',
  direction = 'column',
  gutter = 'none',
  padding,
  spacing = 12,
  skeletons,
  ref: forwardedRef,
  fade = true,
  ...remaining
}: SkeletonGroupProps) {
  const id = useId();

  const baseCSS: CSS = {
    flexDirection: direction,
  };
  const mergeCSS = merge(
    baseCSS,
    gutterCSS({ gutter, context }),
    paddingCSS(padding),
    spacingCSS(spacing),
  );

  return (
    <SkeletonGroupContainer ref={forwardedRef} css={mergeCSS} {...remaining}>
      {skeletons.map((skeleton, index) => {
        const opacity =
          fade && skeletons.length > 3
            ? ((skeletons.length - 1 - index) / (skeletons.length - 1)) * 0.9 + 0.1
            : 1;
        const key = index;
        return <Skeleton key={`${id}-skeleton-${key}`} opacity={opacity} {...skeleton} />;
      })}
    </SkeletonGroupContainer>
  );
}
